import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { StatusBadge, EmptyState } from '@/components/Shared';
import { DangerConfirm } from '@/components/DangerConfirm';
import { BalanceBadge } from '@/pages/Customers';
import { useAuth } from '@/lib/auth';
import { money, shortDate, dateTime } from '@/lib/format';
import { ArrowLeft, Briefcase, Users2, Receipt, Wallet, TrendingDown, CircleDollarSign, FileText, Plus, Download, Eraser, Power, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export const PAYMENT_METHODS = ['Cash', 'Check', 'Zelle', 'Venmo', 'Bank Transfer', 'Other'];

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-4"><div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">{label}</span><span className={`flex h-8 w-8 items-center justify-center rounded-lg ${accent}`}><Icon className="h-4 w-4" /></span></div><div className="mt-2 text-xl font-semibold tabular-nums font-heading">{value}</div></Card>
  );
}

export default function CustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('all');
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: '', method: 'Cash', note: '' });
  const [clearOpen, setClearOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [delPay, setDelPay] = useState(null);
  const [delPayBusy, setDelPayBusy] = useState(false);
  const doDeletePayment = async (password) => {
    setDelPayBusy(true);
    try {
      await api.post(`/payments/${delPay.id}/delete`, { password, confirm: 'CONFIRM' });
      toast.success(delPay.kind === 'refund' ? 'Refund deleted' : 'Payment deleted');
      setDelPay(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setDelPayBusy(false); }
  };

  const reload = useCallback(() => { api.get(`/customers/${id}`).then(({ data }) => setData(data)).catch(() => {}); }, [id]);
  useEffect(() => { reload(); }, [reload]);

  const toggleActive = async () => {
    try { await api.put(`/customers/${id}`, { active: data.active === false }); toast.success(data.active === false ? 'Customer activated' : 'Customer deactivated'); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const clearBalance = async (password) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/customers/${id}/clear-balance`, { password, confirm: 'CONFIRM' });
      toast.success(res.cleared ? `Balance of ${money(res.cleared)} cleared` : 'Balance already settled');
      setClearOpen(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const recordPayment = async () => {
    if (!payForm.amount || Number(payForm.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      await api.post(`/customers/${id}/payments`, { amount: Number(payForm.amount), method: payForm.method, note: payForm.note });
      toast.success('Payment recorded — balance updated');
      setPayOpen(false); setPayForm({ amount: '', method: 'Cash', note: '' }); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const downloadPdf = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: 'blob' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(res.data);
      link.download = filename; link.click();
      window.URL.revokeObjectURL(link.href);
    } catch { toast.error('Download failed'); }
  };

  if (!data) return <div className="p-8 text-center text-muted-foreground">Loading…</div>;
  const s = data.stats;
  const isEmployee = data.type === 'employee';
  const invoices = data.invoices || [];
  const filtered = tab === 'all' ? invoices
    : tab === 'paid' ? invoices.filter((i) => i.payment_status === 'Paid')
    : invoices.filter((i) => i.payment_status !== 'Paid');

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/customers')} data-testid="customer-back-button"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadPdf(`/customers/${id}/statement.pdf`, `statement-${data.name}.pdf`)} data-testid="customer-statement-button"><Download className="h-4 w-4 mr-1" /> Statement</Button>
          {isAdmin && !data.is_default && <Button variant="outline" size="sm" onClick={toggleActive} data-testid="customer-deactivate-button"><Power className="h-4 w-4 mr-1" /> {data.active === false ? 'Activate' : 'Deactivate'}</Button>}
          {isAdmin && <Button variant="outline" size="sm" onClick={() => setClearOpen(true)} data-testid="customer-clear-balance-button"><Eraser className="h-4 w-4 mr-1" /> Clear Balance</Button>}
          {isAdmin && <Button size="sm" onClick={() => setPayOpen(true)} data-testid="customer-record-payment-button"><Plus className="h-4 w-4 mr-1" /> Record Payment</Button>}
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${isEmployee ? 'bg-teal-50 text-teal-700' : 'bg-blue-50 text-blue-700'}`}>{isEmployee ? <Briefcase className="h-6 w-6" /> : <Users2 className="h-6 w-6" />}</div>
          <div>
            <h1 className="font-heading text-2xl font-semibold tracking-tight" data-testid="customer-profile-name">{data.name}</h1>
            <p className="text-sm text-muted-foreground">{isEmployee ? 'Employee customer' : 'Regular customer'} · {data.discount_percent || 0}% discount{data.email ? ` · ${data.email}` : ''}</p>
          </div>
        </div>
        <BalanceBadge status={s.balance_status} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Receipt} label="Total Invoices" value={s.total_invoices} accent="bg-slate-100 text-slate-700" />
        <Stat icon={CircleDollarSign} label="Total Sales" value={money(s.total_invoiced)} accent="bg-emerald-50 text-emerald-700" />
        <Stat icon={Wallet} label="Paid Amount" value={money(s.total_paid)} accent="bg-teal-50 text-teal-700" />
        <Stat icon={TrendingDown} label="Balance Due" value={money(s.balance_due)} accent="bg-amber-50 text-amber-700" />
      </div>

      {isEmployee && (
        <Card className="p-5 mt-4" data-testid="employee-settlement-card">
          <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><Briefcase className="h-4 w-4" /> Employee Settlement</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div><div className="text-muted-foreground">Total Sales</div><div className="font-semibold tabular-nums">{money(s.total_invoiced)}</div></div>
            <div><div className="text-muted-foreground">Discount Received</div><div className="font-semibold tabular-nums">{money(s.total_discount)}</div></div>
            <div><div className="text-muted-foreground">Balance Due</div><div className="font-semibold tabular-nums">{money(s.balance_due)}</div></div>
            <div><div className="text-muted-foreground">Settlement Status</div><div className="mt-0.5"><BalanceBadge status={s.balance_status} /></div></div>
          </div>
        </Card>
      )}

      <Card className="p-0 overflow-hidden mt-4">
        <div className="p-4 border-b flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="font-heading font-semibold">Invoices</h3>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="all" data-testid="customer-invoices-tab-all">All ({invoices.length})</TabsTrigger>
              <TabsTrigger value="unpaid" data-testid="customer-invoices-tab-unpaid">Unpaid</TabsTrigger>
              <TabsTrigger value="paid" data-testid="customer-invoices-tab-paid">Paid</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        {filtered.length === 0 ? <div className="p-6"><EmptyState icon={FileText} title="No invoices" /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Invoice #</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Original</TableHead>
                <TableHead className="text-right">Discount</TableHead><TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Paid</TableHead><TableHead className="text-right">Balance</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map((i) => (
                  <TableRow key={i.id} className="cursor-pointer" data-testid="customer-invoice-row" onClick={() => navigate(`/invoices/${i.id}`)}>
                    <TableCell className="font-medium">{i.invoice_number}</TableCell>
                    <TableCell>{shortDate(i.created_at)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(i.subtotal)}</TableCell>
                    <TableCell className="text-right tabular-nums text-amber-700">{money(i.discount_amount || 0)}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{money(i.total)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(i.amount_paid)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(Math.max(0, i.total - i.amount_paid))}</TableCell>
                    <TableCell><StatusBadge kind="payment" value={i.payment_status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Card className="p-5 mt-4">
        <h3 className="font-heading font-semibold mb-3">Payment History</h3>
        {(data.payments || []).length === 0 ? <p className="text-sm text-muted-foreground">No payments recorded yet.</p> : (
          <div className="space-y-2">
            {data.payments.map((p) => {
              const isRefund = p.kind === 'refund' || Number(p.amount) < 0;
              return (
              <div key={p.id} className="flex items-center justify-between rounded-lg border bg-white p-3 text-sm" data-testid="customer-payment-row">
                <div><span className="font-medium">{isRefund ? 'Refund' : (p.invoice_number || 'Account payment')}</span> · {p.method}<div className="text-xs text-muted-foreground">{dateTime(p.created_at)}{p.note ? ` · ${p.note}` : ''}</div></div>
                <div className="flex items-center gap-2">
                  <span className={`font-semibold tabular-nums ${isRefund ? 'text-red-600' : 'text-emerald-700'}`}>{isRefund ? '- ' : ''}{money(Math.abs(Number(p.amount)))}</span>
                  {isAdmin && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDelPay(p)} data-testid="payment-delete-button"><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </Card>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-md" data-testid="record-payment-dialog">
          <DialogHeader><DialogTitle className="font-heading">Record Payment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">This payment reduces {data.name}'s running balance. Current balance due: <b>{money(s.balance_due)}</b></p>
            <div><Label>Amount ($)</Label><Input type="number" min="0" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="mt-1.5" data-testid="payment-amount-input" /></div>
            <div><Label>Method</Label>
              <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                <SelectTrigger className="mt-1.5" data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Note (optional)</Label><Textarea value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button><Button onClick={recordPayment} data-testid="payment-submit-button">Record Payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <DangerConfirm
        open={clearOpen}
        onOpenChange={setClearOpen}
        title={`Clear balance for ${data.name}?`}
        description="This books an adjustment that sets the customer's running balance to $0. Existing invoices and payments are kept for history."
        affectedText={`Current balance: ${money(data.stats?.balance_due ?? 0)}. It will be adjusted to $0.00.`}
        onConfirm={clearBalance}
        busy={busy}
        confirmLabel="Clear Balance"
      />

      <DangerConfirm
        open={!!delPay}
        onOpenChange={(o) => !o && setDelPay(null)}
        title={delPay?.kind === 'refund' ? 'Delete this refund?' : 'Delete this payment?'}
        description="Permanently deletes this record and recomputes the linked invoice status and customer balance. This cannot be undone."
        affectedText={delPay ? `${delPay.kind === 'refund' ? 'Refund' : 'Payment'} · ${delPay.method} · ${money(Math.abs(Number(delPay.amount)))}` : ''}
        onConfirm={doDeletePayment}
        busy={delPayBusy}
        confirmLabel={delPay?.kind === 'refund' ? 'Delete Refund' : 'Delete Payment'}
      />
    </div>
  );
}
